import type { TimelineRecord } from '@/features/timeline/model/timeline-record';
import type { TimelineRepository } from '@/features/timeline/application/ports/timeline-repository';
import { TimelineCatalogUseCases } from './timeline-catalog-use-cases';
import { TimelineEditorUseCases } from './timeline-editor-use-cases';
import type {
  TimelineCommandRequest,
  TimelineEditorState,
} from './timeline-editor-types';
import type { TimelineFileService, OpenFileResult, SaveFileResult } from '../infrastructure/timeline-file-service';
import type { FileHandleStore } from '../infrastructure/file-handle-store';
import type { RecentFilesService } from './recent-files';

type OnOptimistic = (state: TimelineEditorState) => void;

const FILE_WATCH_INTERVAL = 2000;

export class TimelineUseCases {
  private readonly catalogUseCases: TimelineCatalogUseCases;
  readonly editor: TimelineEditorUseCases;
  private readonly fileHandles = new Map<string, FileSystemFileHandle>();
  private readonly fileWatchers = new Map<string, { timer: ReturnType<typeof setInterval>; lastModified: number }>;

  constructor(
    private readonly repository: TimelineRepository,
    private readonly fileService: TimelineFileService,
    private readonly recentFiles: RecentFilesService,
    private readonly fileHandleStore: FileHandleStore,
  ) {
    this.catalogUseCases = new TimelineCatalogUseCases(repository);
    this.editor = new TimelineEditorUseCases({
      findById: (id) => this.catalogUseCases.findById(id),
      applyChanges: (id, input) => this.repository.applyChanges(id, input),
    });
  }

  // --- File operations ---

  async openFile(): Promise<OpenFileResult | null> {
    const result = await this.fileService.openFile();
    if (!result) return null;

    this.repository.register(result.record);
    this.fileHandles.set(result.record.id, result.handle);
    this.recentFiles.addOrUpdate({
      fileName: result.fileName,
      lastOpenedAt: new Date().toISOString(),
    });
    await this.fileHandleStore.save(result.record.id, result.fileName, result.handle).catch(() => {});
    return result;
  }

  async openRecentFile(fileName: string): Promise<OpenFileResult | null> {
    const entry = await this.fileHandleStore.getByFileName(fileName);
    if (!entry) return null;

    const result = await this.fileService.openFromHandle(entry.handle);
    if (!result) return null;

    this.repository.register(result.record);
    this.fileHandles.set(result.record.id, result.handle);
    this.recentFiles.addOrUpdate({
      fileName: result.fileName,
      lastOpenedAt: new Date().toISOString(),
    });
    await this.fileHandleStore.save(result.record.id, result.fileName, result.handle).catch(() => {});
    return result;
  }

  async openById(id: string): Promise<OpenFileResult | null> {
    const entry = await this.fileHandleStore.getById(id);
    if (!entry) return null;

    const result = await this.fileService.openFromHandle(entry.handle);
    if (!result) return null;

    this.repository.register(result.record);
    this.fileHandles.set(result.record.id, result.handle);
    this.recentFiles.addOrUpdate({
      fileName: result.fileName,
      lastOpenedAt: new Date().toISOString(),
    });
    return result;
  }

  async saveFile(id: string, handle?: FileSystemFileHandle): Promise<SaveFileResult | null> {
    const record = await this.repository.findById(id);
    if (!record) return null;

    const effectiveHandle = handle ?? this.fileHandles.get(id);
    const result = await this.fileService.saveFile(record, effectiveHandle);
    if (result) {
      this.fileHandles.set(id, result.handle);
      this.recentFiles.addOrUpdate({
        fileName: result.fileName,
        lastOpenedAt: new Date().toISOString(),
      });
      await this.fileHandleStore.save(id, result.fileName, result.handle).catch(() => {});
      // Update watcher's lastModified so we don't detect our own save as external change
      await this.syncFileWatchTimestamp(id, result.handle);
    }
    return result;
  }

  async saveFileAs(id: string, suggestedName?: string): Promise<SaveFileResult | null> {
    const record = await this.repository.findById(id);
    if (!record) return null;

    const result = await this.fileService.saveFileAs(record, suggestedName ?? this.fileHandles.get(id)?.name);
    if (result) {
      this.fileHandles.set(id, result.handle);
      this.recentFiles.addOrUpdate({
        fileName: result.fileName,
        lastOpenedAt: new Date().toISOString(),
      });
      await this.fileHandleStore.save(id, result.fileName, result.handle).catch(() => {});
      await this.syncFileWatchTimestamp(id, result.handle);
    }
    return result;
  }

  getFileHandle(id: string): FileSystemFileHandle | undefined {
    return this.fileHandles.get(id);
  }

  // --- Catalog operations ---

  async findById(id: string): Promise<TimelineRecord | null> {
    return this.catalogUseCases.findById(id);
  }

  async createTimeline(): Promise<TimelineRecord> {
    return this.catalogUseCases.createTimeline();
  }

  // --- Editor session lifecycle ---

  async createEditorSession(id: string): Promise<TimelineEditorState | null> {
    return this.editor.createEditorSession(id);
  }

  disposeEditorSession(id: string): void {
    this.editor.disposeEditorSession(id);
  }

  async refreshEditorSession(id: string): Promise<TimelineEditorState | null> {
    return this.editor.refreshEditorSession(id);
  }

  // --- Editor commands (delegate to editor) ---

  executeCommand(id: string, request: TimelineCommandRequest, onOptimistic?: OnOptimistic): Promise<TimelineEditorState | null> {
    return this.editor.executeCommand(id, request, onOptimistic);
  }

  addSection(id: string, onOptimistic?: OnOptimistic): Promise<TimelineEditorState | null> {
    return this.editor.addSection(id, onOptimistic);
  }

  removeSection(id: string, sectionId: string, onOptimistic?: OnOptimistic): Promise<TimelineEditorState | null> {
    return this.editor.removeSection(id, sectionId, onOptimistic);
  }

  moveSection(id: string, sectionId: string, toIndex: number, onOptimistic?: OnOptimistic): Promise<TimelineEditorState | null> {
    return this.editor.moveSection(id, sectionId, toIndex, onOptimistic);
  }

  updateSectionLabel(id: string, sectionId: string, nextLabel: string, onOptimistic?: OnOptimistic): Promise<TimelineEditorState | null> {
    return this.editor.updateSectionLabel(id, sectionId, nextLabel, onOptimistic);
  }

  updateItemTitle(id: string, sectionId: string, itemId: string, nextTitle: string, onOptimistic?: OnOptimistic): Promise<TimelineEditorState | null> {
    return this.editor.updateItemTitle(id, sectionId, itemId, nextTitle, onOptimistic);
  }

  createItem(id: string, sectionId: string, draft: { title: string; startDate: string; endDate?: string }, onOptimistic?: OnOptimistic): Promise<TimelineEditorState | null> {
    return this.editor.createItem(id, sectionId, draft, onOptimistic);
  }

  changeItemDateRange(id: string, itemId: string, nextRange: { sectionId: string; startDate: string; endDate?: string }, onOptimistic?: OnOptimistic): Promise<TimelineEditorState | null> {
    return this.editor.changeItemDateRange(id, itemId, nextRange, onOptimistic);
  }

  replaceWithSnapshot(id: string, snapshot: TimelineRecord['data'], onOptimistic?: OnOptimistic): Promise<TimelineEditorState | null> {
    return this.editor.replaceWithSnapshot(id, snapshot, onOptimistic);
  }

  hideSection(id: string, sectionId: string, onOptimistic?: OnOptimistic): Promise<TimelineEditorState | null> {
    return this.editor.hideSection(id, sectionId, onOptimistic);
  }

  showSection(id: string, sectionId: string, onOptimistic?: OnOptimistic): Promise<TimelineEditorState | null> {
    return this.editor.showSection(id, sectionId, onOptimistic);
  }

  deleteItem(id: string, sectionId: string, itemId: string, onOptimistic?: OnOptimistic): Promise<TimelineEditorState | null> {
    return this.editor.deleteItem(id, sectionId, itemId, onOptimistic);
  }

  undo(id: string, onOptimistic?: OnOptimistic): Promise<TimelineEditorState | null> {
    return this.editor.undo(id, onOptimistic);
  }

  redo(id: string, onOptimistic?: OnOptimistic): Promise<TimelineEditorState | null> {
    return this.editor.redo(id, onOptimistic);
  }

  // --- File watching ---

  startFileWatch(id: string, onChange: (record: TimelineRecord) => void): void {
    this.stopFileWatch(id);

    const handle = this.fileHandles.get(id);
    if (!handle) return;

    const watcher = {
      timer: setInterval(() => void this.pollFile(id, handle, onChange), FILE_WATCH_INTERVAL),
      lastModified: 0,
    };

    // Initialize lastModified
    void handle.getFile().then((file) => {
      watcher.lastModified = file.lastModified;
    }).catch(() => {});

    this.fileWatchers.set(id, watcher);
  }

  stopFileWatch(id: string): void {
    const watcher = this.fileWatchers.get(id);
    if (watcher) {
      clearInterval(watcher.timer);
      this.fileWatchers.delete(id);
    }
  }

  private async syncFileWatchTimestamp(id: string, handle: FileSystemFileHandle): Promise<void> {
    const watcher = this.fileWatchers.get(id);
    if (!watcher) return;
    try {
      const file = await handle.getFile();
      watcher.lastModified = file.lastModified;
    } catch {
      // ignore
    }
  }

  private async pollFile(
    id: string,
    handle: FileSystemFileHandle,
    onChange: (record: TimelineRecord) => void,
  ): Promise<void> {
    const watcher = this.fileWatchers.get(id);
    if (!watcher) return;

    try {
      const file = await handle.getFile();
      if (watcher.lastModified === 0) {
        watcher.lastModified = file.lastModified;
        return;
      }
      if (file.lastModified === watcher.lastModified) return;

      watcher.lastModified = file.lastModified;

      const result = await this.fileService.readFile(handle);
      this.repository.register(result.record);
      onChange(result.record);
    } catch {
      // Permission lost or file deleted — stop watching silently
      this.stopFileWatch(id);
    }
  }

  // --- Recent files ---

  getRecentFiles() {
    return this.recentFiles.list();
  }

  removeRecentFile(fileName: string) {
    this.recentFiles.remove(fileName);
  }
}
